import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import { Stack, Tooltip, Typography } from '@mui/material';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import axiosInstance from 'src/utils/axios';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteEmployee,
  getBaseSalaryRange,
  getPaginatedEmployees,
  removeEmployee,
} from 'src/redux/slices/employees';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  EmployeeStatus,
  EMPLOYEE_EPF_STATUS,
  IEmployeeBaseSalaryRange,
  IEmployeeListItem,
  IEmployeeTableFilters,
  IEmployeeTableFilterValue,
  IEmployeeTableSort,
} from 'src/types/employee';

import EmployeeTableRow from '../employee-table-row';
import EmployeeTableToolbar from '../employee-table-toolbar';
import PrintEmployeesDialog from '../print-employees-dialog';

const TABLE_HEAD = [
  { id: 'firstName', label: 'Name', align: 'left', primary: true },
  { id: 'baseSalary', label: 'Base Salary', align: 'left', primary: true },
  { id: 'epf', label: 'EPF', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IEmployeeTableFilters = {
  generalSearch: '',
  epf: EMPLOYEE_EPF_STATUS.ALL,
  baseSalary: null,
  status: EmployeeStatus.ACTIVE,
};

const defaultSortBy: IEmployeeTableSort = {
  sortBy: 'employeeNumber',
  order: 'asc',
};

const DEFAULT_ROWS_PER_PAGE = 25;

const SORTABLE_COLUMNS = TABLE_HEAD.map(({ id }) => id).concat('employeeNumber');

// The list state (page, filters, sort) lives in the URL, so coming back from
// an employee's details page lands on the same page instead of the first.
const readPositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export default function EmployeeListView() {
  const { status, employees, error, baseSalary } = useAppSelector((state) => state.employees);

  const salaryRange: IEmployeeBaseSalaryRange[] = baseSalary.data;

  const { user: authUser } = useAuthContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const table = useTable({
    defaultRowsPerPage: readPositiveInt(searchParams.get('rowsPerPage'), DEFAULT_ROWS_PER_PAGE),
    defaultCurrentPage: readPositiveInt(searchParams.get('page'), 1) - 1,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState<IEmployeeTableFilters>(() => {
    const epf = searchParams.get('epf');
    const statusParam = searchParams.get('status');
    const salaryParam = searchParams.get('baseSalary');

    return {
      generalSearch: searchParams.get('search') ?? defaultFilters.generalSearch,
      epf: Object.values(EMPLOYEE_EPF_STATUS).includes(epf as EMPLOYEE_EPF_STATUS)
        ? (epf as EMPLOYEE_EPF_STATUS)
        : defaultFilters.epf,
      baseSalary:
        salaryParam !== null && Number.isFinite(Number(salaryParam)) ? Number(salaryParam) : null,
      status: Object.values(EmployeeStatus).includes(statusParam as EmployeeStatus)
        ? (statusParam as EmployeeStatus)
        : defaultFilters.status,
    };
  });

  const [sort, setSort] = useState<IEmployeeTableSort>(() => {
    const sortBy = searchParams.get('sortBy');

    return {
      sortBy: sortBy && SORTABLE_COLUMNS.includes(sortBy) ? sortBy : defaultSortBy.sortBy,
      order: searchParams.get('order') === 'desc' ? 'desc' : defaultSortBy.order,
    };
  });

  useEffect(() => {
    const next = new URLSearchParams();

    if (page > 0) next.set('page', String(page + 1));
    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) next.set('rowsPerPage', String(rowsPerPage));
    if (filters.generalSearch) next.set('search', filters.generalSearch);
    if (filters.epf !== defaultFilters.epf) next.set('epf', filters.epf);
    if (filters.baseSalary !== null) next.set('baseSalary', String(filters.baseSalary));
    if (filters.status !== defaultFilters.status) next.set('status', filters.status);
    if (sort.sortBy !== defaultSortBy.sortBy) next.set('sortBy', sort.sortBy);
    if (sort.order !== defaultSortBy.order) next.set('order', sort.order);

    // Replace, so paging around doesn't pile up history entries.
    setSearchParams(next, { replace: true });
  }, [page, rowsPerPage, filters, sort, setSearchParams]);

  const canReset = !isEqual(defaultFilters, filters);

  const canPrint = hasPermission(
    authUser,
    'any',
    AuthPermissions.DOWNLOAD_EMPLOYEE_REPORT,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  // Picked employees survive searching, filtering and paging, so a list can
  // be built up across several searches.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const printDialog = useBoolean();

  // With two or more employees ticked the top Print button prints just those
  // (after picking fields); otherwise it prints the whole active list.
  const printsSelection = selectedIds.length >= 2;

  const handleFilters = useCallback(
    (name: string, value: IEmployeeTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.employees.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedEmployees = useCallback(() => {
    dispatch(
      getPaginatedEmployees({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        sort: sort.order,
        sortBy: sort.sortBy,
        epf:
          filters.epf === EMPLOYEE_EPF_STATUS.ALL
            ? null
            : filters.epf === EMPLOYEE_EPF_STATUS.ENABLED,
        baseSalary: filters.baseSalary,
        status: filters.status,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (employee: IEmployeeListItem, index: number) => {
      dispatch(removeEmployee({ employeeId: employee._id }));

      try {
        await dispatch(deleteEmployee({ employee, index })).unwrap();

        fetchPaginatedEmployees();
        enqueueSnackbar('Employee deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedEmployees]
  );

  const fetchSalaryRange = useCallback(() => {
    dispatch(getBaseSalaryRange());
  }, [dispatch]);

  const downloadEmployeeReport = useCallback(async () => {
    try {
      const response = await axiosInstance.get('employee/download', {
        params: {
          status: filters.status,
        },
        responseType: 'blob',
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'Employee_List.csv';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      enqueueSnackbar('Failed to download employee report', { variant: 'error' });
    }
  }, [filters.status]);

  const downloadSelectedEmployeeReport = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        'employee/download-selected',
        { employeeIds: selectedIds },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'Selected_Employees.csv';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      enqueueSnackbar('Failed to download selected employees', { variant: 'error' });
    }
  }, [selectedIds]);

  // Printable PDF of active employees only, regardless of the current
  // filters (the CSV above follows the Status filter).
  const downloadEmployeePdf = useCallback(async () => {
    try {
      const response = await axiosInstance.get('employee/generate-report', {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/pdf',
        },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'Employee_List.pdf';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      enqueueSnackbar('Failed to download employee PDF', { variant: 'error' });
    }
  }, []);

  const pageIds = useMemo(
    () => employees.employees.map((row: IEmployeeListItem) => row._id),
    [employees.employees]
  );

  const selectedOnPage = pageIds.filter((id: string) => selectedIds.includes(id)).length;

  const handleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAllRows = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) =>
        checked
          ? [...prev, ...pageIds.filter((id: string) => !prev.includes(id))]
          : prev.filter((id) => !pageIds.includes(id))
      );
    },
    [pageIds]
  );

  const printSelectedEmployees = useCallback(
    async (fields: string[]) => {
      try {
        const response = await axiosInstance.post(
          'employee/print-selected',
          { employeeIds: selectedIds, fields },
          { responseType: 'blob' }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'Selected_Employees.pdf';
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
      } catch (e) {
        enqueueSnackbar('Failed to print selected employees', { variant: 'error' });
        throw e;
      }
    },
    [selectedIds]
  );

  useEffect(() => {
    fetchPaginatedEmployees();
    fetchSalaryRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedEmployees, fetchSalaryRange]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Employees"
        links={[]}
        action={
          <Stack direction="row" gap={2}>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.DOWNLOAD_EMPLOYEE_REPORT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip
                title={
                  printsSelection
                    ? `Download ${selectedIds.length} Selected Employees (CSV)`
                    : 'Download Employee Report'
                }
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={
                    printsSelection ? downloadSelectedEmployeeReport : downloadEmployeeReport
                  }
                >
                  <Iconify icon="eva:arrow-circle-down-fill" width={24} />
                </Button>
              </Tooltip>
            )}

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.DOWNLOAD_EMPLOYEE_REPORT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip
                title={
                  printsSelection
                    ? `Print ${selectedIds.length} Selected Employees (PDF)`
                    : 'Print Active Employees (PDF)'
                }
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={printsSelection ? printDialog.onTrue : downloadEmployeePdf}
                >
                  <Iconify icon="eva:file-text-fill" width={24} />
                </Button>
              </Tooltip>
            )}

            {(hasPermission(
              authUser,
              'all',
              AuthPermissions.CREATE_EMPLOYEE,
              AuthPermissions.UPLOAD_EMPLOYEE
            ) ||
              hasPermission(
                authUser,
                'any',
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              )) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.employees.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Employee
              </Button>
            )}
          </Stack>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <EmployeeTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
          salaryRange={salaryRange}
        />

        {selectedIds.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 1.5, bgcolor: 'action.selected' }}
          >
            <Typography variant="subtitle2">{selectedIds.length} selected</Typography>

            <Button color="inherit" onClick={() => setSelectedIds([])}>
              Clear
            </Button>
          </Stack>
        )}

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={employees.employees.length}
                numSelected={selectedOnPage}
                onSelectAllRows={canPrint ? handleSelectAllRows : undefined}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {employees?.employees.map((row: IEmployeeListItem, index: number) => (
                      <EmployeeTableRow
                        key={row._id}
                        row={row}
                        selected={selectedIds.includes(row._id)}
                        onSelectRow={canPrint ? () => handleSelectRow(row._id) : undefined}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && employees.count === 0 && (
                  <TableNoData message="No employees found" notFound={employees.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={employees.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={(value) => {
            table.onChangeDense(value);
          }}
        />
      </Card>

      {printDialog.value && (
        <PrintEmployeesDialog
          isOpen={printDialog.value}
          employeeCount={selectedIds.length}
          isAdmin={isAdmin}
          onClose={printDialog.onFalse}
          onPrint={printSelectedEmployees}
        />
      )}
    </Container>
  );
}
